/**
 * AI-Music Detector component for the home page.
 *
 * Author: Bounlith Sirivong <bounlith.sirivong@ircamamplify.com>
 * Copyright (c) Ircam Amplify 2025-2026
 */

import HomeModule from '@/components/HomePageModule/HomeModule'
import * as Def from '@/components/HomePageModule/HomeModuleDef'
import { ModuleConfigList } from '@/components/ModuleConfig/ModuleConfigList'
import { ModuleKey } from '@/components/ModuleConfig/ModuleInfo'
import '@/components/styles/Input.css'
import { downloader } from '@/config/downloader'
import { logger, LogTag } from '@/config/logger'
import { TierType } from '@amplify3/duplo-back-end'
import { fetchAuthSession } from 'aws-amplify/auth'
import Cookies from 'js-cookie'
import { useCallback, useEffect, useMemo, useState } from 'react'
import AiMusicDetectorParamComp from './AiMusicDetectorParamComp'
import {
  AiMusicDetectorCookie,
  AiMusicDetectorResult,
} from './AiMusicDetectorResult'
import HomeAiMusicDetectorResult from './HomeAiMusicDetectorResult'

interface HomeAiMusicDetectorProps {
  id?: string
  tier: TierType
}

const HomeAiMusicDetector = (props: HomeAiMusicDetectorProps) => {
  const [detectionResult, setDetectionResult] = useState<
    AiMusicDetectorResult | undefined
  >(undefined)

  // The list of the parameters defined in ModuleDef.tsx,
  // for Ai Music Detector.
  const [moduleParamList, setModuleParamList] = useState<Record<string, any>>(
    {}
  )

  const costNumber = TierType.TopUpCreditAimd == props.tier ? '1' : '0.1'

  useEffect(() => {
    logger.debug(LogTag.HomePage, 'moduleParamList', moduleParamList)
  }, [moduleParamList])

  // Use useMemo to avoid creating a new reference every render
  const stableModuleParamList = useMemo(
    () => moduleParamList,
    [moduleParamList]
  )

  // Use useCallback to prevent unnecessary re-renders
  const updateModuleParamList = useCallback((newParam: Record<string, any>) => {
    setModuleParamList((prev) => ({
      ...prev,
      ...newParam,
    }))
  }, [])

  function createProcessingResult() {
    return new AiMusicDetectorResult()
  }

  async function handleProcessingResult(
    response: any,
    jobId: string,
    fileName?: string
  ) {
    const result = response.job_infos.report_info!.report.resultList[0]

    const detectionResult = new AiMusicDetectorResult({
      aiProbability: result.aiProbability,
      inputFileName: fileName,
      iasUrl: result.ias,
      jobId: jobId,
      processingIsoDate: response.job_infos.job_start_datetime,
      suspectedModel:
        result.suspectedModel != 'none' ? result.suspectedModel : undefined,
      modelVersion:
        result.modelVersion != 'none' ? result.modelVersion : undefined,
      userId: Cookies.get(Def.COOKIE_USER_ID),
    })

    if (result.timeAnalysis?.id) {
      logger.debug(LogTag.HomePage, 'Download time analysis...')
      const session = await fetchAuthSession()
      const json = JSON.parse(
        await downloader.asyncGetContentFromIas(
          session,
          result.timeAnalysis?.id
        )
      )
      logger.debug(LogTag.HomePage, 'Time analysis data downloaded:', json)
      detectionResult.timeAnalysis = json
    }

    setDetectionResult(detectionResult)
    detectionResult.save()
  }

  function cleanSavedData() {
    AiMusicDetectorResult.cleanSavedData()
  }

  function getSavedIasUrl() {
    return Cookies.get(AiMusicDetectorCookie.IasUrl)
  }

  function getSavedFileName() {
    return Cookies.get(AiMusicDetectorCookie.FileName)
  }

  return (
    <HomeModule
      id={props.id}
      title="AI Music Detector"
      description="Identify AI-generated tracks."
      iconSrc="/icon/artificial-intelligence.svg"
      iconAlt="Artificial intelligence"
      costText={`${costNumber} credit/file`}
      moduleConfig={ModuleConfigList[ModuleKey.AiMusicDetector]}
      moduleParamComp={AiMusicDetectorParamComp}
      updateModuleParamList={updateModuleParamList}
      moduleParamList={stableModuleParamList}
      handleProcessingResult={handleProcessingResult}
      createProcessingResult={createProcessingResult}
      setProcessingResult={setDetectionResult}
      processingResult={detectionResult}
      resultComponent={HomeAiMusicDetectorResult}
      cleanSavedData={cleanSavedData}
      getSavedIasUrl={getSavedIasUrl}
      getSavedFileName={getSavedFileName}
      restartButtonMsg="Analyze another track"
    />
  )
}

export default HomeAiMusicDetector
